import {ChangeEvent, FC, KeyboardEvent, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {isAxiosError} from 'axios';
import {useFetchBanks} from '@rocket-ui/lk-api';
import {TextInput} from 'features/registration/ui/components/TextInput';
import {BIK_NOT_FOUND_MESSAGE, extractBikDigits, formatBikDisplay, isBikValid} from '../../model/bik';
import {bankFromApi, type SelectedBank} from '../../model/selectedBank';
import styles from './AccountBikStep.module.css';

type Props = {
	value: string;
	selectedBank: SelectedBank | null;
	onChange: (bikDigits: string) => void;
	onBankSelect: (bank: SelectedBank | null) => void;
};

const caretPosAfterDigits = (display: string, digitCount: number): number => {
	if (digitCount <= 0) {
		return 0;
	}

	let seen = 0;

	for (let i = 0; i < display.length; i++) {
		if (/\d/.test(display[i]!)) {
			seen += 1;

			if (seen === digitCount) {
				return i + 1;
			}
		}
	}

	return display.length;
};

export const AccountBikStep: FC<Props> = ({value, selectedBank, onChange, onBankSelect}) => {
	const bikDigits = extractBikDigits(value);
	const bikComplete = isBikValid(bikDigits);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const [caretDigits, setCaretDigits] = useState<number | null>(null);

	const {
		data: banks = [],
		isLoading,
		isFetched,
		isError,
		error,
	} = useFetchBanks(bikComplete ? {bik: bikDigits} : undefined, {
		enabled: bikComplete,
		retry: false,
	});

	const notFoundByStatus = isAxiosError(error) && error.response?.status === 404;
	const showNotFound = bikComplete && !isLoading && (notFoundByStatus || (isFetched && !isError && banks.length === 0));

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const input = event.target;
		const nextRaw = input.value;
		const nextBik = extractBikDigits(nextRaw);
		const caret = input.selectionStart ?? nextRaw.length;
		const digitsBeforeCaret = extractBikDigits(nextRaw.slice(0, caret)).length;

		onChange(nextBik);
		setCaretDigits(digitsBeforeCaret);

		if (nextBik !== bikDigits) {
			onBankSelect(null);
		}
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Backspace') {
			return;
		}

		const input = event.currentTarget;
		const caret = input.selectionStart ?? 0;
		const selectionEnd = input.selectionEnd ?? 0;

		if (caret !== selectionEnd || caret === 0) {
			return;
		}

		const display = formatBikDisplay(bikDigits);
		const prevChar = display[caret - 1];

		if (prevChar !== ' ') {
			return;
		}

		event.preventDefault();

		const digitsBeforeCaret = extractBikDigits(display.slice(0, caret)).length;

		if (digitsBeforeCaret === 0) {
			return;
		}

		const nextDigits = bikDigits.slice(0, digitsBeforeCaret - 1) + bikDigits.slice(digitsBeforeCaret);
		onChange(nextDigits);
		onBankSelect(null);
		setCaretDigits(digitsBeforeCaret - 1);
	};

	useLayoutEffect(() => {
		if (caretDigits === null || !inputRef.current) {
			return;
		}

		const pos = caretPosAfterDigits(formatBikDisplay(bikDigits), caretDigits);
		inputRef.current.setSelectionRange(pos, pos);
		setCaretDigits(null);
	}, [bikDigits, caretDigits]);

	useEffect(() => {
		if (!bikComplete || banks.length === 0) {
			return;
		}

		const next = bankFromApi(banks[0]!);

		if (selectedBank?.id === next.id) {
			return;
		}

		onBankSelect(next);
	}, [banks, bikComplete, onBankSelect, selectedBank?.id]);

	const resolvedBanks = useMemo(() => {
		if (!bikComplete) {
			return [];
		}

		return banks;
	}, [banks, bikComplete]);

	return (
		<div className={styles.root}>
			<h2 className={styles.title}>Введите БИК банка</h2>

			<div className={styles.field}>
				<TextInput
					ref={inputRef}
					name="paymentBik"
					className={styles.input}
					value={formatBikDisplay(value)}
					placeholder="000 000 000"
					inputMode="numeric"
					autoComplete="off"
					autoFocus
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onClear={() => {
						onChange('');
						onBankSelect(null);
					}}
				/>

				{bikComplete && isLoading ? <p className={styles.status}>Загрузка…</p> : null}

				{showNotFound ? <p className={styles.error}>{BIK_NOT_FOUND_MESSAGE}</p> : null}

				{!isLoading && resolvedBanks.length > 0 ? (
					<ul className={styles.banks} aria-label="Найденный банк">
						{resolvedBanks.map((bank) => (
							<li key={bank.id} className={styles.bankRow}>
								{bank.iconUrl ? (
									<img className={styles.bankIcon} src={bank.iconUrl} alt="" />
								) : (
									<span className={styles.bankIconFallback} aria-hidden />
								)}
								<span className={styles.bankName}>{bank.shortName ?? bank.officialName}</span>
							</li>
						))}
					</ul>
				) : null}
			</div>
		</div>
	);
};
